/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: This file was automatically migrated to TypeScript by `dev typescript migrate`.
//  The eslint disable comment above should be removed and the resulting errors fixed.
/*
 * decaffeinate suggestions:
 * DS102: Remove unnecessary code created because of implicit returns
 * DS207: Consider shorter variations of null checks
 * Full docs: https://github.com/decaffeinate/decaffeinate/blob/master/docs/suggestions.md
 */
import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {NucleotideType} from 'biojs/BioConstants';
import {Sequence} from 'components/Sequence';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import {Chromosome, Genome} from 'constants/GenomeConstants';
import {SequenceTopology} from 'constants/SequenceConstants';
import FolderSelector, {DEFAULT_PROJECT_FOLDER_LABEL_TEXT} from 'file-browser/FolderSelector';
import GenomeRegionSelector from 'molbio/genome/GenomeRegionSelector';
import GenomeSelector from 'molbio/genome/GenomeSelector';
import {DNA_SEQUENCE_IMPORT_FLOW} from 'molbio/MolBioConstants';
import notifications from 'notifications';
import GenomeStore from 'stores/GenomeStore';
import FormGroup from 'ui-library/bs/FormGroup';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {formatThousands} from 'util/NameUtil';

import {importChromosomalRegions} from './ImportSequenceDAO';

interface Props {
  initialGenomeName: string;
  initialFolderId?: string;
}

interface State {
  genomeName: string;
  chromosome: Chromosome | null;
  start: string;
  end: string;
  folderId: string | undefined;
  genomes: Array<Genome>;
}

class ChromosomeImportForm extends React.Component<Props, State> {
  static defaultProps = {
    initialGenomeName: 'GRCm38',
  };

  constructor(props: Props) {
    super(props);
    const genomes = GenomeStore.getGenomes();

    this.state = {
      genomeName: props.initialGenomeName,
      chromosome: null,
      start: '',
      end: '',
      folderId: props.initialFolderId,
      genomes,
    };
  }

  notifyFailedImport(errorMessage: string): void {
    notifications.error(errorMessage);
    analytics.track('sequence', 'import_fail', {
      product: ANALYTICS_MODULE.MOLBIO,
      importFlow: DNA_SEQUENCE_IMPORT_FLOW.CHROMOSOME,
      errors: [errorMessage],
    });
  }

  _handleChangeChromosome = (chromosome: any): void => {
    this.setState({chromosome});
    analytics.track('sequence', 'select_files', {
      product: ANALYTICS_MODULE.MOLBIO,
      importFlow: DNA_SEQUENCE_IMPORT_FLOW.CHROMOSOME,
      chromosomalRegion: chromosome.name,
      genome: this.state.genomeName,
    });
  };

  _handleChangeFolderId = (e: any): void => {
    this.setState({folderId: e.target.value});
  };

  getSelectedGenome = (): Genome => _.findWhere(this.state.genomes, {ensemblName: this.state.genomeName})!;

  createSequence = async (): Promise<Sequence | undefined> => {
    analytics.track('sequence', 'import_click', {
      product: ANALYTICS_MODULE.MOLBIO,
      importFlow: DNA_SEQUENCE_IMPORT_FLOW.CHROMOSOME,
    });
    if (this.state.folderId == null) {
      this.notifyFailedImport('Select a folder to import.');
      return;
    }
    const startString = this.state.start.replace(/[, ]/g, '');
    const endString = this.state.end.replace(/[, ]/g, '');

    const start = parseInt(startString, 10);
    const end = parseInt(endString, 10);
    const genome = this.getSelectedGenome();
    const {chromosome} = this.state;
    if (chromosome == null) {
      this.notifyFailedImport('Select a region of the genome to import.');
      return;
    }
    if (!(1 <= start && start <= end && end <= chromosome.length)) {
      this.notifyFailedImport(`Start and end must be between 1 and ${chromosome.name}'s length \
(${formatThousands(chromosome.length)}), inclusive. \
Start cannot be higher than end.`);
      return;
    }

    try {
      const {sequences} = await importChromosomalRegions(
        [{name: chromosome.name, start, end, cdsCoords: []}],
        {
          folderId: this.state.folderId,
          nucleotideType: NucleotideType.DNA,
          topology: SequenceTopology.LINEAR,
        },
        genome.ensemblName,
        false
      );
      analytics.track('sequence', 'import_success', {
        product: ANALYTICS_MODULE.MOLBIO,
        importFlow: DNA_SEQUENCE_IMPORT_FLOW.CHROMOSOME,
        sequenceIds: [sequences[0].id],
      });
      return sequences[0];
    } catch (error) {
      analytics.track('sequence', 'import_fail', {
        product: ANALYTICS_MODULE.MOLBIO,
        importFlow: DNA_SEQUENCE_IMPORT_FLOW.CHROMOSOME,
        errors: [notifications.getFriendlyError(error)],
      });
      throw error;
    }
  };

  handleGenomeChange = (genomeName: any): void => {
    this.setState({
      genomeName,
      chromosome: null,
    });
  };

  getGenome = (): Genome | undefined => _.findWhere(this.state.genomes, {ensemblName: this.state.genomeName});

  render(): JSX.Element {
    return (
      <div>
        <FormGroup>
          <FieldLabel label="Genome" />
          <GenomeSelector
            genomes={this.state.genomes}
            genomeLink={{value: this.state.genomeName, requestChange: this.handleGenomeChange}}
          />
          <p className="help-block u-small-spacing-top">
            Gene, exon, and CDS annotations are included from Ensembl.
          </p>
        </FormGroup>
        <FormGroup>
          <FieldLabel label="Region" />
          <div className={css(styles.region)}>
            <GenomeRegionSelector
              className={css(styles.regionChildLarge)}
              value={this.state.chromosome}
              onChange={this._handleChangeChromosome}
              genome={this.getSelectedGenome()}
            />
            <input
              className={`u-small-spacing-left ${css(styles.regionChildSmall)}`}
              value={this.state.start}
              onChange={(e) => {
                this.setState({start: e.target.value});
              }}
              placeholder="Start"
              type="text"
            />
            <input
              value={this.state.end}
              onChange={(e) => {
                this.setState({end: e.target.value});
              }}
              placeholder="End"
              className={`u-small-spacing-left ${css(styles.regionChildSmall)}`}
              type="text"
            />
          </div>
          <p className="help-block u-small-spacing-top">
            The positions are 1-indexed, and the end is inclusive. The current size limit is 5 Mb.
          </p>
        </FormGroup>
        <FormGroup>
          <FieldLabel label={DEFAULT_PROJECT_FOLDER_LABEL_TEXT} />
          <FolderSelector
            showInventory={true}
            value={this.state.folderId}
            onChange={this._handleChangeFolderId}
            unsetOnFetchFail={true}
          />
        </FormGroup>
      </div>
    );
  }
}

export default ChromosomeImportForm;

const styles = StyleSheet.create({
  region: {
    display: 'flex',
  },
  regionChildLarge: {
    flex: 3,
  },
  regionChildSmall: {
    flex: 1,
  },
});
